import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getDateLocale } from "@/lib/dateFormat";
import type { Meeting } from "@/types";

interface MeetingCardProps {
  meeting: Meeting;
  onDelete: (id: string) => void;
}

const statusConfig: Record<string, { color: string }> = {
  recorded: { color: "text-redi-warning" },
  transcribing: { color: "text-redi-warning" },
  summarizing: { color: "text-redi-warning" },
  done: { color: "text-redi-success" },
  error: { color: "text-redi-error" },
};

const statusLabels: Record<string, string> = {
  recorded: "meetingCard.statusPending",
  transcribing: "meetingCard.statusTranscribing",
  summarizing: "meetingCard.statusSummarizing",
  done: "meetingCard.statusDone",
  error: "meetingCard.statusError",
};

export function MeetingCard({ meeting, onDelete }: MeetingCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const date = new Date(meeting.recorded_at).toLocaleDateString(
    getDateLocale(),
    { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }
  );

  const duration = meeting.duration_seconds
    ? `${Math.floor(meeting.duration_seconds / 60)} min`
    : null;

  const config = statusConfig[meeting.status] ?? statusConfig.recorded;

  return (
    <div
      onClick={() => navigate(`/meeting/${meeting.id}`)}
      className="bg-redi-surface rounded-xl p-4 cursor-pointer hover:bg-redi-surface/80 transition-colors group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-redi-text font-semibold truncate">
            {meeting.title || t("meetings.untitled")}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs font-medium ${config.color}`}>
              {t(statusLabels[meeting.status])}
            </span>
            <span className="text-redi-muted text-xs">{date}</span>
            {duration && (
              <span className="text-redi-muted text-xs">{duration}</span>
            )}
          </div>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm(t("meetingDetail.deleteMsg"))) {
              onDelete(meeting.id);
            }
          }}
          className="opacity-0 group-hover:opacity-100 p-2 text-redi-muted hover:text-redi-error rounded-lg transition-all"
          title={t("common.delete")}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
